import { AggregateOperationDtoAggregateFunctionEnum, ColumnDto, ColumnDtoTypeEnum, DatabaseRelationDto, FilterDtoOperatorEnum, GroupByDto } from "apps/marsinet/src/app/typescript-axios";
import { Column } from "primereact/column";


export type ColumnAliasMapping = Record<string, string>;


function getColumnKey(column: ColumnDto): string {
    return `${column.table}.${column.column}`
}


// Function to get the column aliases

export function mapColumnAlias(columns: ColumnDto[], groupBy: GroupByDto): ColumnAliasMapping {
    let columnAliasMapping: ColumnAliasMapping = Object.fromEntries(
        columns.map((column) => {
            const original_column = getColumnKey(column);
            let column_alias = `${column.table}_${column.column}`;

            if (groupBy?.date_trunc_unit && column.type === ColumnDtoTypeEnum.Timestamp) {
                column_alias = `${column.table}_${column.column}_${groupBy.date_trunc_unit}`;
            }

            return [original_column, column_alias];
        })
    );

    groupBy?.aggregate_operations?.forEach(({ column, aggregate_function }) => {
        const original_column = getColumnKey(column);
        columnAliasMapping[original_column] = `${aggregate_function}_${column.table}_${column.column}`;
    });

    return columnAliasMapping;
}


// Functions to get the available operators for context

export function getAvailableFilterOperators(columnType: ColumnDtoTypeEnum): FilterDtoOperatorEnum[] {
    const sharedOperators = [
        FilterDtoOperatorEnum.IsNull,
        FilterDtoOperatorEnum.IsNotNull,
        FilterDtoOperatorEnum.In,
        FilterDtoOperatorEnum.NotIn,
    ];

    const typeOperators: Record<ColumnDtoTypeEnum, FilterDtoOperatorEnum[]> = {
        [ColumnDtoTypeEnum.Other]: [
            FilterDtoOperatorEnum.Like,
            FilterDtoOperatorEnum.NotLike,
            FilterDtoOperatorEnum.Ilike,
            FilterDtoOperatorEnum.NotIlike,
            FilterDtoOperatorEnum.Regexp,
            FilterDtoOperatorEnum.NotRegexp,
        ],
        [ColumnDtoTypeEnum.Number]: [
            FilterDtoOperatorEnum.Eq,
            FilterDtoOperatorEnum.Neq,
            FilterDtoOperatorEnum.Gt,
            FilterDtoOperatorEnum.Gte,
            FilterDtoOperatorEnum.Lt,
            FilterDtoOperatorEnum.Lte,
            FilterDtoOperatorEnum.Between,
            FilterDtoOperatorEnum.NotBetween,
        ],
        [ColumnDtoTypeEnum.Timestamp]: [
            FilterDtoOperatorEnum.Eq,
            FilterDtoOperatorEnum.Neq,
            FilterDtoOperatorEnum.Gt,
            FilterDtoOperatorEnum.Gte,
            FilterDtoOperatorEnum.Lt,
            FilterDtoOperatorEnum.Lte,
            FilterDtoOperatorEnum.Between,
            FilterDtoOperatorEnum.NotBetween,
        ],
    };

    return [...sharedOperators, ...(typeOperators[columnType] ?? [])];
}

export function getAvailableAggregateFunction(columnType: ColumnDtoTypeEnum): AggregateOperationDtoAggregateFunctionEnum[] {
    const allTypes = [
        AggregateOperationDtoAggregateFunctionEnum.Count,
        AggregateOperationDtoAggregateFunctionEnum.CountDistinct,
    ];

    const numericOnly = [
        AggregateOperationDtoAggregateFunctionEnum.Max,
        AggregateOperationDtoAggregateFunctionEnum.Min,
        AggregateOperationDtoAggregateFunctionEnum.Sum,
        AggregateOperationDtoAggregateFunctionEnum.Avg,
    ];

    switch (columnType) {
        case ColumnDtoTypeEnum.Number:
            return [...allTypes, ...numericOnly];

        case ColumnDtoTypeEnum.Timestamp:
        case ColumnDtoTypeEnum.Other:
            return allTypes;
    }
}


// Functions to get available options

export function getAvailableTableOptions(baseTables: Record<string, ColumnDto[]>, baseTablesRelations: DatabaseRelationDto[], selectedTables: string[]): string[] {
    const allTables = Object.keys(baseTables);

    if (selectedTables.length === 0) return allTables;

    const relatedTables = new Set<string>(selectedTables);

    baseTablesRelations.forEach((relation) => {
        const pkTable = relation.primary_key.table;
        const fkTable = relation.foreign_key.table;

        if (selectedTables.includes(pkTable)) relatedTables.add(fkTable);
        if (selectedTables.includes(fkTable)) relatedTables.add(pkTable);
    });

    return allTables.filter((table) => relatedTables.has(table));
};

export function getAvailableBaseColumnsOptions(baseTables: Record<string, ColumnDto[]>, selectedTables: string[]): Record<string, ColumnDto> {
    if (!selectedTables || !baseTables) {
        return {}
    }

    const allAvailableColumns: ColumnDto[] = selectedTables
        .flatMap(table => baseTables[table] ?? []);

    const columnAliases = mapColumnAlias(allAvailableColumns, undefined);

    return allAvailableColumns.reduce<Record<string, ColumnDto>>((acc, column) => {
        const key = getColumnKey(column);
        const alias = columnAliases[key];
        if (alias) {
            acc[alias] = column;
        }
        return acc;
    }, {});
};

export function getAggregatableColumnOptions(baseTables: Record<string, ColumnDto[]>, selectedTables: string[], group_by_columns: ColumnDto[]): Record<string, ColumnDto> {
    const all = getAvailableBaseColumnsOptions(baseTables, selectedTables);

    const excludedAliases = new Set(
        Object.entries(all)
            .filter(([, dto]) =>
                (group_by_columns ?? []).some(
                    (col) => col.table === dto.table && col.column === dto.column
                )
            )
            .map(([alias]) => alias)
    );

    return Object.fromEntries(
        Object.entries(all).filter(([alias]) => !excludedAliases.has(alias))
    );
}

export function getPostGroupingColumnOptions(
    baseTables: Record<string, ColumnDto[]>,
    selectedTables: string[],
    selectedColumns: ColumnDto[],
    groupBy: GroupByDto
): Record<string, ColumnDto> {

    if (!selectedColumns || selectedColumns.length === 0) {
        return getAvailableBaseColumnsOptions(baseTables, selectedTables);
    }

    const aggregatedColumns: ColumnDto[] = (groupBy?.aggregate_operations ?? []).map(aggregateOp => aggregateOp.column)

    const allColumns = [...selectedColumns, ...aggregatedColumns];
    const aliasMap = mapColumnAlias(allColumns, groupBy);

    return allColumns.reduce((acc, column) => {
        const alias = aliasMap[getColumnKey(column)];
        if (alias) {
            acc[alias] = column;
        }
        return acc;
    }, {} as Record<string, ColumnDto>);
}